import type { SelectOption } from '../../../components/SelectCustom'

export type AddressLabels = {
  title: string
  subtitle: string
  addAddress: string
  emptyTitle: string
  emptyDescription: string
  emptyCta: string
  shippingBadge: string
  billingBadge: string
  defaultBadge: string
  defaultShippingState: string
  defaultBillingState: string
  setDefaultShipping: string
  setDefaultBilling: string
  edit: string
  remove: string
  removeConfirm: string
  addFormTitle: string
  editFormTitle: string
  addFormSubtitle: string
  label: string
  firstName: string
  lastName: string
  addressLine: string
  city: string
  postalCode: string
  province: string
  country: string
  phone: string
  useShipping: string
  useBilling: string
  setDefault: string
  saveNew: string
  saveEdit: string
  cancel: string
  requiredError: string
  duplicateError: string
  saveSuccess: string
  saveError: string
  roleHint: string
  noPhone: string
}

export const buildAddressLabels = (isItalian: boolean): AddressLabels => ({
  title: isItalian ? 'I tuoi indirizzi' : 'Your addresses',
  subtitle: isItalian
    ? 'Gestisci gli indirizzi di spedizione e fatturazione'
    : 'Manage shipping and billing addresses',
  addAddress: isItalian ? 'Aggiungi indirizzo' : 'Add address',
  emptyTitle: isItalian ? 'Non hai ancora salvato indirizzi' : 'No saved addresses yet',
  emptyDescription: isItalian
    ? 'Aggiungi un indirizzo per velocizzare il checkout'
    : 'Add an address to speed up checkout.',
  emptyCta: isItalian ? 'Aggiungi indirizzo' : 'Add address',
  shippingBadge: isItalian ? 'Spedizione' : 'Shipping',
  billingBadge: isItalian ? 'Fatturazione' : 'Billing',
  defaultBadge: isItalian ? 'Predefinito' : 'Default',
  defaultShippingState: isItalian ? 'Predefinito spedizione' : 'Default shipping',
  defaultBillingState: isItalian ? 'Predefinito fatturazione' : 'Default billing',
  setDefaultShipping: isItalian ? 'Imposta default spedizione' : 'Set default shipping',
  setDefaultBilling: isItalian ? 'Imposta default fatturazione' : 'Set default billing',
  edit: isItalian ? 'Modifica' : 'Edit',
  remove: isItalian ? 'Elimina' : 'Delete',
  removeConfirm: isItalian
    ? 'Vuoi davvero eliminare questo indirizzo?'
    : 'Do you really want to delete this address?',
  addFormTitle: isItalian ? 'Nuovo indirizzo' : 'New address',
  editFormTitle: isItalian ? 'Modifica indirizzo' : 'Edit address',
  addFormSubtitle: isItalian
    ? 'Compila i dati e scegli come usare questo indirizzo.'
    : 'Fill in the details and choose how to use this address.',
  label: isItalian ? 'Etichetta (opzionale)' : 'Label (optional)',
  firstName: isItalian ? 'Nome' : 'First name',
  lastName: isItalian ? 'Cognome' : 'Last name',
  addressLine: isItalian ? 'Indirizzo' : 'Address',
  city: isItalian ? 'Città' : 'City',
  postalCode: isItalian ? 'CAP' : 'Postal code',
  province: isItalian ? 'Provincia' : 'Province',
  country: isItalian ? 'Paese' : 'Country',
  phone: isItalian ? 'Telefono' : 'Phone',
  useShipping: isItalian ? 'Usa come indirizzo di spedizione' : 'Use as shipping address',
  useBilling: isItalian ? 'Usa come indirizzo di fatturazione' : 'Use as billing address',
  setDefault: isItalian ? 'Imposta come predefinito' : 'Set as default',
  saveNew: isItalian ? 'Salva indirizzo' : 'Save address',
  saveEdit: isItalian ? 'Aggiorna indirizzo' : 'Update address',
  cancel: isItalian ? 'Annulla' : 'Cancel',
  requiredError: isItalian
    ? 'Compila i campi obbligatori: nome, cognome, indirizzo, città, CAP e paese.'
    : 'Fill required fields: first name, last name, address, city, postal code, and country.',
  duplicateError: isItalian
    ? 'Esiste già un indirizzo con questi dati.'
    : 'An address with the same details already exists.',
  saveSuccess: isItalian ? 'Indirizzi aggiornati correttamente.' : 'Addresses saved successfully.',
  saveError: isItalian ? 'Impossibile aggiornare gli indirizzi.' : 'Unable to update addresses.',
  roleHint: isItalian
    ? 'Seleziona almeno un ruolo per usarlo al checkout.'
    : 'Pick at least one role to use it at checkout.',
  noPhone: isItalian ? 'Telefono non disponibile' : 'Phone not available',
})

export const buildCountryOptions = (isItalian: boolean): SelectOption[] => [
  { value: 'IT', label: isItalian ? 'Italia' : 'Italy' },
  { value: 'FR', label: isItalian ? 'Francia' : 'France' },
  { value: 'DE', label: isItalian ? 'Germania' : 'Germany' },
  { value: 'ES', label: isItalian ? 'Spagna' : 'Spain' },
  { value: 'PT', label: isItalian ? 'Portogallo' : 'Portugal' },
  { value: 'US', label: isItalian ? 'Stati Uniti' : 'United States' },
  { value: 'GB', label: isItalian ? 'Regno Unito' : 'United Kingdom' },
]

export const buildCountryLabelMap = (countryOptions: SelectOption[]) => {
  const map = new Map<string, string>()
  countryOptions.forEach((option) => {
    map.set(option.value, option.label)
  })
  return map
}

export const LocationIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth={1.8} aria-hidden>
    <path d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10Z" />
    <circle cx="12" cy="11" r="2.2" />
  </svg>
)
